import { ChangeDetectionStrategy, Component, computed, input, output, Signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
    faArrowDown,
    faArrowUp,
    faBan,
    faBox,
    faExchangeAlt,
    faExclamationTriangle,
    faFileInvoice,
    faPhone,
    faUsers,
    IconDefinition,
} from '@fortawesome/free-solid-svg-icons';

import { PaceStatusBadgeComponent } from '../pace-status-badge/pace-status-badge.component';
import { PaceNotificationCard, PaceNotificationSeverity } from './pace-notification-card.interface';

/** Arrow icon and its color class per severity — the card's footer indicator for the equipment state. */
const SEVERITY_ICONS: Record<PaceNotificationSeverity, { icon: IconDefinition; colorClass: string } | undefined> = {
    down: { icon: faArrowDown, colorClass: 'red' },
    partiallyDown: { icon: faExchangeAlt, colorClass: 'orange' },
    working: { icon: faArrowUp, colorClass: 'green' },
    unknown: undefined,
};

/**
 * Presentational card for one notification on the dashboard.
 *
 * Everything it renders comes from {@link PaceNotificationCard}; the only thing it reports back
 * is that the user selected it.
 */
@Component({
    selector: 'pace-notification-card',
    imports: [FontAwesomeModule, PaceStatusBadgeComponent],
    templateUrl: './pace-notification-card.component.html',
    styleUrls: ['./pace-notification-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaceNotificationCardComponent {
    public readonly card = input.required<PaceNotificationCard>();

    public readonly cardSelect = output<PaceNotificationCard>();

    public readonly faExclamationTriangle = faExclamationTriangle;
    public readonly faBan = faBan;
    public readonly faFileInvoice = faFileInvoice;
    public readonly faBox = faBox;
    public readonly faParticipants = faUsers;
    public readonly faPhone = faPhone;

    public readonly severityIcon: Signal<IconDefinition | undefined> = computed(
        () => SEVERITY_ICONS[this.card().severity]?.icon,
    );
    public readonly severityColorClass: Signal<string> = computed(
        () => SEVERITY_ICONS[this.card().severity]?.colorClass ?? '',
    );

    /**
     * The last line of the card is clipped to a single line so a long text cannot push the
     * footer out of the card. Only the *last* rendered line gets clipped, so this reports
     * whether any of the lines below the affected-equipment / clarification lines exist.
     */
    public readonly hasContentBelowClarifyText: Signal<boolean> = computed(() => {
        const card = this.card();
        return card.noOfOrderedMaterials > 0 || card.formsAvailable.length > 0 || card.noOfParticipants > 0;
    });

    public onSelect(): void {
        this.cardSelect.emit(this.card());
    }
}
